import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import type { GridTableHeaderItem} from '@uikit';
import { GridTable, Spinner } from '@uikit';

import type {
  CampaignParticipant,
  DeserializedZealyRef,
  StormCampaignParticipant,
  ZealyStoryTaskParticipant,
} from '@api';

import { MsqToken } from '@uikit/icons/MsqToken';

import { useAuth } from '@shared/common/providers/AuthProvider';
import truncate from '@shared/helpers/web3/truncate';

import { useStormCampaignSelfParticipant, useStormTradeParticipantListInfiniteQuery } from '@query/story';

export const getParticipantPosition = ({
  participant,
}: {
  participant: CampaignParticipant;
  index: number;
}) => {
  return `#${participant.attributes.position}`;
};

export const ZealyParticipantXp = ({
  participant,
  className,
}: {
  participant: CampaignParticipant;
  className?: string;
}) => {
  return (
    <span
      className={classNames(
        'inline-flex justify-center items-center text-xs rounded-full px-2 py-1 min-h-[1.5rem] bg-primary-100',
        className,
      )}
    >
      {participant.attributes.xp} XP
    </span>
  );
};

export const ZealyParticipantReward = ({
  participant,
  currency,
  className,
}: {
  participant: ZealyStoryTaskParticipant | DeserializedZealyRef;
  currency?: string;
  className?: string;
}) => {
  return (
    <span
      className={classNames(
        'inline-flex justify-center items-center text-xs rounded-full gap-1 px-2 py-1 min-h-[1.5rem] bg-secondary text-neutral-50',
        className,
      )}
    >
      <MsqToken className="icon-2xs" />
      {Math.trunc(+(participant.attributes.reward || 0))} {currency || ''}
    </span>
  );
};

const tableHeader: GridTableHeaderItem<CampaignParticipant, { self?: StormCampaignParticipant }>[] =
  [
    {
      label: 'Position',
      name: 'position',
      bodyCellClassName: 'max-sm:hidden',
      render: ({ rowIndex, item }) => {
        return getParticipantPosition({
          participant: item,
          index: rowIndex,
        });
      },
    },
    {
      label: 'User',
      name: 'user',
      bodyCellClassName: 'flex sm:items-center gap-2 max-sm:w-full',
      render: ({ item, rowIndex, meta }) => {
        const position = getParticipantPosition({
          participant: item,
          index: rowIndex,
        });

        const isSelf = item.id === meta?.self?.id;

        const evmAddress = item.attributes.address;
        const tonAddress = item.attributes.addressExtra;
        const formattedEvmAddress = isSelf || !evmAddress ? evmAddress : truncate(evmAddress);
        const formattedTonAddress =
          isSelf || !tonAddress ? tonAddress : truncate(tonAddress, { start: 4 });

        return (
          <>
            <span className="sm:hidden">{position}</span>

            <ImageProxy
              alt="Avatar"
              src={item.attributes.icon}
              width={44}
              height={44}
              className="flex-shrink-0 rounded-full w-6 h-6 object-cover"
            />

            <div className="flex gap-2 w-full items-center flex-wrap overflow-hidden">
              <span className="flex-grow flex-wrap flex text-sm text-neutral-500">
                {!!evmAddress && (
                  <span className="truncate" title={evmAddress}>
                    {formattedEvmAddress}
                  </span>
                )}

                {!isSelf && !!evmAddress && !!tonAddress && ' / '}

                {!!tonAddress && (
                  <span className="truncate" title={tonAddress}>
                    {formattedTonAddress}
                  </span>
                )}
              </span>
              <ZealyParticipantXp className="sm:hidden whitespace-nowrap" participant={item} />
            </div>
          </>
        );
      },
    },
    {
      label: 'Xp',
      name: 'xp',
      bodyCellClassName: 'max-sm:hidden whitespace-nowrap',
      render: ({ item }) => {
        return <ZealyParticipantXp participant={item} />;
      },
    },
  ];

const StormTradeStoryLeaderboard = ({ className }: { className?: string }) => {
  const { isAuth } = useAuth();
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useStormTradeParticipantListInfiniteQuery();
  const { data: selfParticipantData } = useStormCampaignSelfParticipant({
    enabled: isAuth,
  });

  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const allParticipants = useMemo(() => {
    const participants = data?.pages.flatMap((page) => page.data) || [];

    if (!selfParticipantData?.id) {
      return participants;
    }

    const hasSelfParticipant = !!participants.find(({ id }) => id === selfParticipantData.id);

    if (!hasSelfParticipant) {
      participants.push(selfParticipantData);
    }

    return participants;
  }, [data, selfParticipantData]);

  return (
    <GridTable
      header={tableHeader}
      className={classNames(
        'overflow-y-auto flex flex-col rounded-t-2xl bg-neutral-50 shadow-card min-h-60',
        className,
      )}
      bodyRowClassName={({ item }) =>
        classNames('border-b border-primary-300 md:min-h-14', {
          'sticky top-0 md:top-10 bg-secondary-50 -order-1':
            selfParticipantData && selfParticipantData?.id === item.id,
        })
      }
      meta={{
        self: selfParticipantData,
      }}
      headRowClassName="bg-primary-200 text-neutral-500 sticky -order-1 top-0 max-sm:hidden"
      rowClassName="gap-2 md:px-6 p-4 md:py-2 flex items-start flex-wrap sm:grid sm:gap-1.5 sm:min-w-min sm:grid-cols-[0.6fr_minmax(60px,1fr)_100px] sm:flex-shrink-0 sm:h-10 sm:py-0 sm:items-center"
      data={allParticipants}
    >
      {!isLoading && !allParticipants?.length && (
        <p className="m-auto text-center text-neutral-200 text-sm">Leaderboard is Empty</p>
      )}

      {(isLoading || hasNextPage) && (
        // Key is added to trigger in view cb again if component is already in view
        <Spinner ref={ref} key={data?.pages.length} className="m-auto" size="md" />
      )}
    </GridTable>
  );
};

export default StormTradeStoryLeaderboard;
